import {
  AnswerSource,
  InsightsChartSpec,
  InsightsContextUsed,
  ProposedAction,
} from 'services/streamInsightsQuery';
import { MessageFeedback } from 'services/insightsFeedback';

export type InsightsChatRole = 'user' | 'assistant';

// Lifecycle of a Phase 3 build proposal in the transcript: awaiting the user's
// decision, committed (created), or dismissed.
export type ProposedActionStatus = 'pending' | 'created' | 'cancelled';

// What a confirmed proposal made, where the agent needs it later: a metric's
// id, so the next turn charts it rather than proposing it again
export interface ProposedActionResult {
  metricId: string;
  metricName: string;
}

// One step in the agent's reasoning trace (staff-only), recorded in arrival
// order so the trace reads reasoning → tool → data_call → reasoning → … A
// `data_call` is an underlying data-engine call the model's tool delegated to
// (a graphql facade tool / gateway aggregation) — nested under its parent tool.
//
// `context_used` is the one entry that is not something the model DID — it is
// what the server put in front of it before it started: the knowledge-base
// version and this account's knowledge (always-on, fired by a trigger, or
// matched-but-capped). When an answer is wrong, it separates "the note never
// reached it" from "it had the note and ignored it".
export type InsightsTraceEntry =
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool'; name: string }
  | { kind: 'data_call'; name: string; args?: string }
  | { kind: 'context_used'; context: InsightsContextUsed };

// Cap each reasoning block on persist so a long multi-turn trace can't push a
// conversation doc toward Firestore's 1 MB limit.
const REASONING_PERSIST_MAX = 4000;

// An edit the agent made to a Studio block in this answer, and how applying
// it went. Summaries only: the cards to undo to stay with the Studio that
// applied them.
export interface InsightsStudioEditRecord {
  editId: string;
  blockId: string;
  summary: string;
  // A block the agent added, rather than changed
  isNew?: boolean;
  // A block the agent moved, resized or removed
  arrangeAction?: 'move' | 'resize' | 'remove';
  // A Composite element the agent changed, added, removed or moved, or a
  // row it moved
  elementAction?: 'edit' | 'add' | 'remove' | 'move' | 'moveRow';
  isApplied: boolean;
  rejected: string[];
  // Why a new block wasn't added (see StudioEditOutcome)
  reason?: 'noMetrics' | 'noRoom' | 'exists';
  missing?: string;
  warnings?: string[];
}

export interface InsightsChatMessage {
  id: string;
  role: InsightsChatRole;
  content: string;
  isLoading?: boolean;
  isError?: boolean;
  // Actionable detail for a failed answer (timeout guidance, budget, expired
  // session). Absent for a generic failure — the error icon + Retry carry it.
  errorMessage?: string;
  // Transient label shown while a tool runs (e.g. "Crunching the numbers…").
  toolStatus?: string;
  // Follow-up questions the agent proposed for this answer (V3).
  suggestions?: string[];
  // Charts the agent rendered for this answer (V4), shown inline under the prose.
  charts?: InsightsChartSpec[];
  // Provenance for the numbers in this answer (metric/date/filters of each
  // aggregate_metric query), shown as an auditable "source" footer.
  sources?: AnswerSource[];
  // A build the agent proposed for this answer (Phase 3) + its confirm state.
  proposedAction?: ProposedAction;
  proposedActionStatus?: ProposedActionStatus;
  proposedActionResult?: ProposedActionResult;
  // The agent's reasoning trace for this answer (staff-only). Only ever
  // populated when the caller is FleetOps staff — customers' messages carry no
  // trace, so the reasoning disclosure never renders for them.
  trace?: InsightsTraceEntry[];
  // The user's feedback on this answer (training loop, P1) — set optimistically
  // when they react, so the control shows their choice.
  feedback?: MessageFeedback;
  // Edits the agent made to the Studio canvas in this answer
  studioEdits?: InsightsStudioEditRecord[];
}

// The persisted shape (Phase 5) — only the durable fields. Transient UI state
// (isLoading/toolStatus) is dropped. Charts store their full spec
// (incl. `source`) so they re-render on revisit without re-querying.
export interface PersistedInsightsMessage {
  id: string;
  role: InsightsChatRole;
  content: string;
  // A failed answer IS durable: a restored conversation shows the failure
  // indicator (and can retry it) rather than a question that silently lost
  // its answer.
  isError?: boolean;
  errorMessage?: string;
  suggestions?: string[];
  charts?: InsightsChartSpec[];
  sources?: AnswerSource[];
  // Carried so a revisited conversation shows the proposal + whether it was
  // created or cancelled. A still-'pending' proposal restores as cancelled (we
  // can't re-open the stream), so it never invites a stale confirm.
  proposedAction?: ProposedAction;
  proposedActionStatus?: ProposedActionStatus;
  proposedActionResult?: ProposedActionResult;
  // The reasoning trace (staff-only), persisted so devs can review it on a saved
  // conversation. Each reasoning block is length-capped on write.
  trace?: InsightsTraceEntry[];
  // The user's feedback on this answer (P1), persisted so a revisited
  // conversation restores their choice.
  feedback?: MessageFeedback;
  studioEdits?: InsightsStudioEditRecord[];
}

export interface InsightsConversation {
  id: string;
  messages: PersistedInsightsMessage[];
  createdOn: string;
  updatedOn: string;
  // User-assigned title (history list). When set, it overrides the derived
  // first-question title.
  customTitle?: string;
}

// Without an undefined `missing`, which Firestore rejects
const compactStudioEdit = (
  edit: InsightsStudioEditRecord,
): InsightsStudioEditRecord => {
  const { missing, warnings, isNew, arrangeAction, elementAction, ...rest } =
    edit;
  return {
    ...rest,
    ...(missing ? { missing } : {}),
    ...(warnings?.length ? { warnings } : {}),
    ...(isNew ? { isNew } : {}),
    ...(arrangeAction ? { arrangeAction } : {}),
    ...(elementAction ? { elementAction } : {}),
  };
};

// Drop everything optional/undefined so Firestore (which rejects undefined) is
// happy and the doc stays minimal.
export const toPersistedMessages = (
  messages: InsightsChatMessage[],
): PersistedInsightsMessage[] =>
  messages
    .filter((message) => !message.isLoading)
    .map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      ...(message.isError ? { isError: true } : {}),
      ...(message.errorMessage ? { errorMessage: message.errorMessage } : {}),
      ...(message.suggestions ? { suggestions: message.suggestions } : {}),
      ...(message.charts ? { charts: message.charts } : {}),
      ...(message.sources ? { sources: message.sources } : {}),
      ...(message.proposedAction
        ? {
            proposedAction: message.proposedAction,
            // A proposal still pending at save time can't be confirmed later
            // (the stream is gone) — persist it as cancelled, not pending.
            proposedActionStatus:
              message.proposedActionStatus === 'created'
                ? 'created'
                : 'cancelled',
            ...(message.proposedActionResult
              ? { proposedActionResult: message.proposedActionResult }
              : {}),
          }
        : {}),
      ...(message.trace?.length
        ? { trace: message.trace.map(truncateTraceEntry) }
        : {}),
      ...(message.feedback ? { feedback: message.feedback } : {}),
      ...(message.studioEdits?.length
        ? { studioEdits: message.studioEdits.map(compactStudioEdit) }
        : {}),
    }));

// Length-cap a reasoning block before persisting;
// tool / data_call entries are tiny and pass through unchanged.
const truncateTraceEntry = (entry: InsightsTraceEntry): InsightsTraceEntry =>
  entry.kind === 'reasoning'
    ? {
        kind: 'reasoning',
        text: entry.text.slice(0, REASONING_PERSIST_MAX),
      }
    : entry;

export const fromPersistedMessages = (
  messages: PersistedInsightsMessage[],
): InsightsChatMessage[] =>
  messages.map((message) => ({
    id: message.id,
    role: message.role,
    content: message.content,
    isError: message.isError,
    errorMessage: message.errorMessage,
    suggestions: message.suggestions,
    charts: message.charts,
    sources: message.sources,
    proposedAction: message.proposedAction,
    proposedActionStatus: message.proposedActionStatus,
    proposedActionResult: message.proposedActionResult,
    trace: message.trace,
    feedback: message.feedback,
    studioEdits: message.studioEdits,
  }));

// Compact descriptor of ONE grounded query: the metric and the scope it was run
// over (date range, plus the trend interval / breakdown when present).
const describeGroundedSource = (source: AnswerSource): string => {
  const name = source.metricName ?? source.metricId;
  const parts = [`${source.startDate}→${source.endDate}`];
  if (source.trendByCalendarInterval) {
    parts.push(source.trendByCalendarInterval);
  }
  if (source.breakdownField) {
    parts.push(`by ${source.breakdownField}`);
  }
  // Carry the query's filters into replayed history so a follow-up drill-down
  // recovers the basis from ground truth (the structured source), not from the
  // model re-reading its own ctx footer.
  if (source.filters?.length) {
    const clauses = source.filters
      .map((f) => `${f.field} ${f.operator} ${f.values.join('/')}`)
      .join('; ');
    parts.push(`filtered: ${clauses}`);
  }
  return `${name} (${parts.join(', ')})`;
};

// The content of a message as replayed to the agent in `history`.
//
// An assistant turn's tool_use / tool_result blocks are dropped once the turn
// ends — only role + text survive into history — so a later turn can't see that
// earlier figures WERE tool-grounded. The model has been observed concluding
// from that absence that it must have fabricated them (and then "correcting" a
// non-mistake). Re-attach a compact grounding marker built from the answer's
// `sources` (the grounding footer, populated for every user, staff or not) so
// the replayed history carries proof the numbers came from live queries.
//
// Only affects what the AGENT sees: the stored message + the on-screen answer
// keep their raw `content` (persistence reads `message.content`, not this).
// What a proposal is called in the note the agent reads back
const describeProposal = (action: ProposedAction): string => {
  switch (action.kind) {
    case 'create_metric':
    case 'create_compound_metric':
      return `creating the metric "${action.metric.name}"`;
    case 'dashboard':
    case 'report':
      return `building the ${action.kind} "${action.title}"`;
    default:
      return 'adding charts to existing content';
  }
};

// What became of the turn's proposal. Without this the agent can't tell a
// confirmed metric from an unconfirmed one, and proposes it again.
const describeProposalOutcome = (
  message: InsightsChatMessage,
): string | undefined => {
  const { proposedAction: action, proposedActionStatus: status } = message;
  if (!action) {
    return undefined;
  }
  const proposal = `You proposed ${describeProposal(action)}`;
  if (status === 'created') {
    const result = message.proposedActionResult;
    return result
      ? `[${proposal}; the user confirmed it and it now exists as metric id ` +
          `${result.metricId}. Use that id; don't propose it again.]`
      : `[${proposal}; the user confirmed it and it was created.]`;
  }
  return status === 'cancelled'
    ? `[${proposal}; the user cancelled it.]`
    : `[${proposal}; it's still awaiting the user's confirmation.]`;
};

// The Studio blocks the turn added or changed, by id, so "that last chart"
// can be resolved later
const describeStudioEdits = (
  message: InsightsChatMessage,
): string | undefined => {
  const applied = (message.studioEdits ?? []).filter((e) => e.isApplied);
  if (applied.length === 0) {
    return undefined;
  }
  const verb = (e: InsightsStudioEditRecord) =>
    e.elementAction === 'move'
      ? 'moved an element in'
      : e.elementAction === 'moveRow'
        ? 'reordered the rows of'
        : e.elementAction === 'edit'
          ? 'changed an element of'
          : e.elementAction === 'add'
            ? 'added an element to'
            : e.elementAction === 'remove'
              ? 'removed an element from'
              : e.arrangeAction === 'move'
                ? 'moved'
                : e.arrangeAction === 'resize'
                  ? 'resized'
                  : e.arrangeAction === 'remove'
                    ? 'removed'
                    : e.isNew
                      ? 'added'
                      : 'changed';
  const parts = applied.map(
    (e) =>
      `${verb(e)} "${e.summary}" (block ${e.blockId})` +
      (e.warnings?.length ? ` (but ${e.warnings.join('; ')})` : ''),
  );
  return `[Studio: ${parts.join('; ')}.]`;
};

export const toHistoryContent = (message: InsightsChatMessage): string => {
  if (message.role !== 'assistant') {
    return message.content;
  }
  const notes = [
    message.sources?.length
      ? `[grounded via tool calls this turn — ${Array.from(
          new Set(message.sources.map(describeGroundedSource)),
        ).join('; ')}. These figures came from live data queries, not memory.]`
      : undefined,
    describeProposalOutcome(message),
    describeStudioEdits(message),
  ].filter((note): note is string => !!note);
  return notes.length
    ? `${message.content}\n\n${notes.join('\n')}`
    : message.content;
};

// Max characters shown for a history-list title before ellipsis.
const TITLE_MAX_LENGTH = 40;

// A short label for a conversation in the history list — the user's custom
// title if set, otherwise the first user question (the gist of the chat),
// trimmed + truncated. Falls back when a conversation has no user message
// (shouldn't happen, but keeps the list robust).
export const getConversationTitle = (
  conversation: Pick<InsightsConversation, 'messages' | 'customTitle'>,
): string => {
  const custom = conversation.customTitle?.trim();
  if (custom) {
    return custom.length > TITLE_MAX_LENGTH
      ? `${custom.slice(0, TITLE_MAX_LENGTH).trimEnd()}…`
      : custom;
  }
  const firstUser = conversation.messages.find((m) => m.role === 'user');
  const text = firstUser?.content.trim();
  if (!text) {
    return 'Untitled conversation';
  }
  return text.length > TITLE_MAX_LENGTH
    ? `${text.slice(0, TITLE_MAX_LENGTH).trimEnd()}…`
    : text;
};
